import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CheckboxTree from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";

function FileManagerPermission() {
  const token = localStorage.getItem("token");
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();
  const organizationId = localStorage.getItem("organizationId");
  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleData, setRoleData] = useState([]);

  // Fetch roles
  const getRoles = async () => {
    if (!token) return;
    try {
      const response = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoleData(response);
    } catch (error) {
      console.error("Error fetching Roles:", error);
      toast.error("Failed to load roles.");
    }
  };

  // Fetch permissions for the selected role
  useEffect(() => {
    getRoles();
    if (selectedRoleId) {
      const fetchRolePermissions = async () => {
        try {
          const formData = { id: selectedRoleId };

          const response = await get(
            `FileManager/FileManagerPermissionList?roleId=${selectedRoleId}&organizationId=${organizationId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.isSuccess) {
            const { permissionList } = response;

            const transformedNodes = transformPermissions(permissionList);
            setNodes(transformedNodes);

            const checkedValues = extractCheckedNodes(transformedNodes);
            setChecked(checkedValues);
          } else {
            console.error(
              "Failed to fetch role permissions:",
              response.message
            );
            setNodes([]); // Clear nodes if the fetch failed
          }
        } catch (error) {
          console.error("Error fetching role permissions:", error);
          toast.error("An error occurred while loading permissions.");
        }
      };

      fetchRolePermissions();
    } else {
      setNodes([]);
      setChecked([]);
      setExpanded([]);
    }
  }, [selectedRoleId]);

  // Transform server response into tree nodes
  const transformPermissions = (permissions) => {
    return permissions.map((perm) => ({
      id: perm.id || 0,
      detailId: perm.detailId || 0,
      value: perm.value,
      label: perm.label,
      status: perm.status,
      children: perm.children ? transformPermissions(perm.children) : [],
    }));
  };

  // Extract initially checked nodes
  const extractCheckedNodes = (nodes) => {
    return nodes.flatMap((node) => {
      const childChecked = extractCheckedNodes(node.children || []);
      return node.status ? [node.value, ...childChecked] : childChecked;
    });
  };
  const onCheckHandler = (checkedNodes) => {
    setChecked(checkedNodes);

    // Update parent and child nodes' states
    const updatedNodes = updateNodesWithCheckState(nodes, checkedNodes);
    setNodes(updatedNodes);
  };
  const updateNodesWithCheckState = (nodes, checkedNodes) => {
    return nodes.map((node) => {
      // Check if the current node itself is checked
      const isChecked = checkedNodes.includes(String(node.value));

      // Recursively update children
      const updatedChildren = node.children
        ? updateNodesWithCheckState(node.children, checkedNodes)
        : [];

      // Check if any child is checked
      //   const anyChildChecked = updatedChildren.some((child) => child.status);

      // Determine if all children are unchecked
      const allChildrenUnchecked = updatedChildren.every(
        (child) => !child.status
      );

      // If all children are unchecked, set parent to false
      const parentStatus = isChecked;

      // Determine if some but not all children are checked
      const someChildrenCheckedNotAll =
        updatedChildren.some((child) => child.status) && !allChildrenUnchecked;

      return {
        ...node,
        status: parentStatus, // Set parent status based on child status or parent checked state
        partial: someChildrenCheckedNotAll, // Set partial state if some but not all children are checked
        children: updatedChildren, // Recursively update children
      };
    });
  };

  const updateParentStatus = (item) => {
    const hasTrueStatus = item.Children.some((c) => c.Status === true);
    const allFalseStatus = item.Children.every((c) => c.Status === false);
    if (hasTrueStatus) {
      item.status = true;
    } else if (allFalseStatus) {
      item.status = false;
    }
    return item;
  };

  const extractPermissions = (nodes) => {
    return nodes.map((node) => {
      if (node.Children && node.Children.length > 0) {
        node.Children = extractPermissions(node.Children);
        node = updateParentStatus(node);
        return {
          id: node.id || 0,
          detailId: node.detailId || 0,
          value: node.value,
          label: node.label,
          status: node.status,
          children: node.children,
        };
      }
      return {
        id: node.id || 0,
        detailId: node.detailId || 0,
        value: node.value,
        label: node.label,
        status: node.status,
        children: node.children || [],
      };
    });
  };

  // Save permissions
  const saveData = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return;
    }
    const updatedPermissions = extractPermissions(nodes);

    const payload = {
      roleId: selectedRoleId,
      permissions: updatedPermissions,
    };

    try {
      const response = await post(`FileManager/SaveFilePermission`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.isSuccess) {
        toast.success(response.message || "Permissions saved successfully!");
      } else {
        toast.error(response.message || "Failed to save permissions.");
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error("An error occurred while saving permissions.");
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">File Manager Role Permissions</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <label className="form-label">Role</label>
              <select
                value={selectedRoleId}
                className="form-select"
                onChange={(e) => setSelectedRoleId(e.target.value)}
              >
                <option value="">Select</option>
                {roleData.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              {nodes.length > 0 ? (
                <>
                  <label className="form-label">File Manager Items</label>
                  <CheckboxTree
                    nodes={nodes}
                    checked={checked}
                    expanded={expanded}
                    onCheck={onCheckHandler}
                    onExpand={(expanded) => setExpanded(expanded)}
                    iconsClass="fa5"
                  />
                  <div className="col-12 mt-3">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-center mt-3">No permissions available</p>
              )}
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
export default FileManagerPermission;
